// src/rms/data/agreementService.js
// ============================================================================
// The operator's own contract text
// ============================================================================
// Installing the starter library once, listing it, and saving an edit against a
// PIN. Nothing here renders a document or sends one -- that is the next stage.
//
// ----------------------------------------------------------------------------
// Where the words live, and when they stop being ours
// ----------------------------------------------------------------------------
// src/rms/agreements/library is the platform's. rms_agreement_templates is the
// operator's. The copy happens exactly once per (document, language) pair and
// is never repeated, because the moment an owner changes a sentence the
// platform's version stops being the truth for that business.
//
// This is the arrangement seed.js already has with operations.addons and
// operations.rateRules -- copied at provisioning, then owned -- and deliberately
// NOT the arrangement rms_vertical_presets has, where the shared row is
// re-read on every load. A preset is configuration. A contract is a promise
// somebody made to a customer, and it must not change under them.
//
// ----------------------------------------------------------------------------
// The install is not signed, and the edit is
// ----------------------------------------------------------------------------
// Installing writes several rows at once. Requiring a PIN for each would mean
// an owner typing it five or six times before reading a line, and at that
// moment nothing has been authored -- the text is still the platform's. The
// first time a human changes a word, that change is signed and named, and the
// trigger in 20260821160000 refuses the update otherwise.
//
// On the local backend there is no trigger and no RLS, so both the role check
// and the signature are presentational -- the same honest framing pinService
// already carries. There is no second user there to protect anything from.
// ============================================================================

import db, { isLocalBackend } from "./index";
import pinService from "./pinService";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import {
  documentsFor,
  docKeysFor,
  LIBRARY_VERSION,
  LIBRARY_LOCALES,
  COVERED_VERTICALS,
} from "../agreements/library";
import { unknownTokens } from "../agreements/tokens";

/** Longest a body may be. Generous -- a full commercial agreement runs about
 *  12,000 characters -- but not unbounded, because this text is copied into
 *  every document ever sent from it. */
export const MAX_BODY_CHARS = 60000;

/** Base language of a locale tag: "fr-CA" and "fr" are the same body. */
const base = (locale) => String(locale || "en").toLowerCase().split(/[-_]/)[0];

/**
 * Which languages this operator gets a starter set in.
 *
 * English always, because it is the language the library is complete in and the
 * one an operator can most easily translate FROM. Plus their own default where
 * the library covers it -- a Montreal operator rents to French and English
 * speakers both, and the document served is chosen by the customer's locale, so
 * having only one of the two would leave half their customers reading the wrong
 * language.
 *
 * A Spanish operator gets English and translates it. That is the documented
 * fallback, and it is better than shipping legal prose nobody has read in a
 * language nobody here can check.
 */
export function installLocalesFor(operator) {
  const own = base(operator?.default_locale);
  const wanted = ["en"];
  if (own !== "en" && LIBRARY_LOCALES.includes(own)) wanted.push(own);
  return wanted;
}

// ----------------------------------------------------------------------------
// Reading
// ----------------------------------------------------------------------------

export async function listTemplates(operatorId) {
  return db.list("agreementTemplates", { operator_id: operatorId }, { orderBy: "doc_key" });
}

/**
 * The rows grouped as the editor shows them: one entry per document, carrying
 * every language that document exists in.
 */
export function groupByDocument(rows = []) {
  const byKey = new Map();
  for (const row of rows) {
    if (!byKey.has(row.doc_key)) {
      byKey.set(row.doc_key, { docKey: row.doc_key, kind: row.kind, locales: [] });
    }
    byKey.get(row.doc_key).locales.push(row);
  }
  for (const entry of byKey.values()) {
    entry.locales.sort((a, b) => (a.locale === "en" ? -1 : b.locale === "en" ? 1 : a.locale.localeCompare(b.locale)));
  }
  return [...byKey.values()];
}

/**
 * The body to serve one customer.
 *
 * Exact locale, then base language, then the operator's default, then English,
 * then whatever single active row exists. The last step matters: an operator who
 * deleted the English version and kept only their own language must still have
 * a contract to send, not a blank.
 */
export function bodyForLocale(rows = [], { locale, operatorLocale } = {}) {
  const active = rows.filter((r) => r.is_active);
  if (!active.length) return null;

  const want = String(locale || "").toLowerCase();
  return (
    active.find((r) => r.locale.toLowerCase() === want) ||
    active.find((r) => base(r.locale) === base(want)) ||
    active.find((r) => base(r.locale) === base(operatorLocale)) ||
    active.find((r) => base(r.locale) === "en") ||
    active[0]
  );
}

/** Documents this trade should have, that this operator does not. */
export function missingDocuments(operator, rows = []) {
  const wanted = docKeysFor(operator?.vertical_key);
  const locales = installLocalesFor(operator);
  const have = new Set(rows.map((r) => `${r.doc_key}|${base(r.locale)}`));
  const missing = [];
  for (const docKey of wanted) {
    for (const locale of locales) {
      if (!have.has(`${docKey}|${locale}`)) missing.push({ docKey, locale });
    }
  }
  return missing;
}

/** Rows whose starter text has been revised since they were copied. */
export function outdatedTemplates(rows = []) {
  return rows.filter((r) => r.library_version > 0 && r.library_version < LIBRARY_VERSION);
}

// ----------------------------------------------------------------------------
// Installing
// ----------------------------------------------------------------------------

// One install per operator at a time.
//
// Check-then-insert is only idempotent against calls that do not overlap, and
// these overlap constantly: React runs effects twice in development, and the
// operator object changes identity on every invalidate(). Both passes read an
// empty library and both install it, which on the local backend produced a
// visible duplicate of every document -- two "English" buttons under each --
// and on Supabase would have been swallowed by the unique constraint and never
// noticed.
//
// So callers share one run. The constraint stays the backstop; this is the
// mechanism, which is the order those two things belong in.
const installing = new Map();

/**
 * Copy any starter document this operator is missing.
 *
 * Called when the Agreements tab opens rather than from seedOperator, which
 * returns early once any asset exists (seed.js:144) and would therefore skip
 * every business provisioned before this shipped.
 *
 * Does NOT go through seed.js createDemo(). Every row that helper writes carries
 * is_demo, and "remove the demo data" must never delete the contract a business
 * hands its customers. rms_agreement_templates has no is_demo column at all,
 * which is what makes that impossible rather than merely unlikely.
 */
export async function ensureTemplates(operator) {
  if (!operator?.id) return createErrorResponse("No business loaded.");

  const inFlight = installing.get(operator.id);
  if (inFlight) return inFlight;

  const run = installOnce(operator).finally(() => installing.delete(operator.id));
  installing.set(operator.id, run);
  return run;
}

async function installOnce(operator) {
  if (!COVERED_VERTICALS.includes(operator.vertical_key)) {
    return createErrorResponse(
      "There is no starter agreement for this trade yet. You can still write one from scratch."
    );
  }

  const existing = await listTemplates(operator.id);
  if (!existing.success) return existing;

  const missing = missingDocuments(operator, existing.data || []);
  if (!missing.length) return createSuccessResponse({ installed: 0 });

  let installed = 0;
  for (const { docKey, locale } of missing) {
    const doc = documentsFor(operator.vertical_key, locale).find((d) => d.docKey === docKey);
    if (!doc) continue;

    const created = await db.create("agreementTemplates", {
      operator_id: operator.id,
      doc_key: doc.docKey,
      kind: doc.kind,
      locale,
      title: doc.title,
      body: doc.body,
      library_version: LIBRARY_VERSION,
      is_active: true,
    });
    // Keep going. One document failing is worth reporting; it is not a reason
    // to leave an operator with a partly-installed library and no way to
    // retry, and the next call picks up whatever is still missing.
    if (created.success) installed += 1;
  }

  return createSuccessResponse({ installed });
}

// ----------------------------------------------------------------------------
// Editing
// ----------------------------------------------------------------------------

/** What is wrong with a body, in a sentence, or null. */
export function validateBody(body) {
  const text = String(body || "").trim();
  if (!text) return "An agreement needs some text.";
  if (text.length > MAX_BODY_CHARS) {
    return `That is ${text.length.toLocaleString()} characters. The limit is ${MAX_BODY_CHARS.toLocaleString()}.`;
  }
  const bad = unknownTokens(text);
  if (bad.length) {
    return `This app cannot fill ${bad.map((t) => `{{${t}}}`).join(", ")}. Placeholders it does not know are printed as-is on the document the customer signs.`;
  }
  return null;
}

/**
 * Save an edited body, signed.
 *
 * The signature is minted first and written onto the row, where the trigger
 * promotes it into rms_staff_actions in the same transaction as the update. A
 * refused update therefore leaves no log entry claiming a save that did not
 * happen -- which is the failure 20260820140000 exists to have fixed, and is
 * not being reintroduced on the one table whose subject is what a customer can
 * be held to.
 */
export async function saveTemplate(operator, templateId, { pin, title, body, memberId }) {
  if (!operator?.id) return createErrorResponse("No business loaded.");
  if (!templateId) return createErrorResponse("No document chosen.");

  const problem = validateBody(body);
  if (problem) return createErrorResponse(problem);
  if (!String(title || "").trim()) return createErrorResponse("A document needs a title.");

  const signed = await pinService.signAction(operator, {
    pin,
    action: "agreement_template.save",
    subjectTable: "rms_agreement_templates",
    subjectId: templateId,
    context: { doc: templateId },
  });
  if (!signed.success) return signed;

  return db.update("agreementTemplates", templateId, {
    title: String(title).trim(),
    body: String(body).trim(),
    reviewed_at: new Date().toISOString(),
    reviewed_by: memberId || signed.member?.id || null,
    last_signature_id: signed.data,
    updated_at: new Date().toISOString(),
  });
}

/**
 * Put the platform's current starter text back.
 *
 * Signed like any other change to the wording, because it IS one: an owner who
 * has spent an afternoon on their damage clause should have that overwrite
 * recorded against a name, exactly as the afternoon's edits were.
 */
export async function resetTemplate(operator, template, { pin }) {
  if (!operator?.id || !template?.id) return createErrorResponse("No document chosen.");

  const doc = documentsFor(operator.vertical_key, template.locale).find(
    (d) => d.docKey === template.doc_key
  );
  if (!doc) {
    return createErrorResponse(
      "There is no standard version of this document to go back to — it was written from scratch."
    );
  }

  const signed = await pinService.signAction(operator, {
    pin,
    action: "agreement_template.save",
    subjectTable: "rms_agreement_templates",
    subjectId: template.id,
    context: { doc: template.id, reset: true },
  });
  if (!signed.success) return signed;

  return db.update("agreementTemplates", template.id, {
    title: doc.title,
    body: doc.body,
    library_version: LIBRARY_VERSION,
    reviewed_at: null,
    reviewed_by: null,
    last_signature_id: signed.data,
    updated_at: new Date().toISOString(),
  });
}

/** Turn a document on or off. Not a change to the wording, so no signature --
 *  see the trigger's own note on why a prompt for a non-event is a prompt
 *  people learn to dismiss. */
export async function setActive(templateId, isActive) {
  return db.update("agreementTemplates", templateId, {
    is_active: !!isActive,
    updated_at: new Date().toISOString(),
  });
}

export default {
  MAX_BODY_CHARS,
  installLocalesFor,
  listTemplates,
  groupByDocument,
  bodyForLocale,
  missingDocuments,
  outdatedTemplates,
  ensureTemplates,
  validateBody,
  saveTemplate,
  resetTemplate,
  setActive,
  isLocalBackend,
};
